import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  VStack,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import Delete from "@root/assets/icons/delete-02.svg";
import { EditSvg } from "@root/assets/icons";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@root/assets/icons/CheckIcon";
import { useDeptCategoryContext } from "@root/context/pledge-deparments";
import { PAGINATION_DEFAULTS, ROLES, USER_TYPES } from "@root/constants";
import { debounce } from "@root/utils";
import { Paginate } from "@root/components/pagination";
import { ModalDelete } from "@root/components/modal-delete";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import SearchInput from "@root/components/common/SearchInput";

export const DeptCategoryManagement = () => {
  const { t } = useTranslation("common");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const user: any = useSelector(getLoggedInUser);

  const {
    fetchCategories,
    categoriesData,
    addCategory,
    newCategory,
    setNewCategory,
    page,
    setPage,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
    search,
    setSearch,
    pageCount,
    addCategoryLoading,
    getCategoriesLoading,
    updateCategoryLoading,
    deleteCategoryLoading,
    updateCategory,
    deleteCategory,
  } = useDeptCategoryContext();

  const handleApiCall = (pageParam: any, searchParam?: any) => {
    const subscriberId =
      user?.role === USER_TYPES.SUBSUBSCRIBER ? user?.subscriberId : user?.id;
    fetchCategories(user?.id, {
      subscriberId: subscriberId,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (pageParam - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search: searchParam ? searchParam : search,
    });
  };

  useEffect(() => {
    handleApiCall(page);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    handleApiCall(newPage, search);
  };


  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(1);
      handleApiCall(page, searchValue);
    }, 500),
    [],
  );

  const handleSearch = (e: any) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleAddCategory = () => {
    const subscriberId =
      user?.role === USER_TYPES.SUBSUBSCRIBER ? user?.subscriberId : user?.id;
    const payload = {
      title: newCategory,
      parentId: 0,
    };
    console.log("payload", payload);

    addCategory(user?.id, payload, subscriberId);
  };

  // const handleDeleteCategory = (cat: any) => {
  //   const id = cat?.id;
  //   deleteCategory(id);
  // };

  const handleDeleteCategory = () => {
    const subscriberId =
      user?.role === USER_TYPES.SUBSUBSCRIBER ? user?.subscriberId : user?.id;
    deleteCategory(user?.id, deleteId, subscriberId);
    setIsDeleteModal(false);
    setDeleteId("");
  };

  const handleSaveEdit = (cat: any) => {
    const subscriberId =
      user?.role === USER_TYPES.SUBSUBSCRIBER ? user?.subscriberId : user?.id;
    const id = cat?.id;
    const payload = {
      title: editingValue,
      parentId: 0,
    };
    updateCategory(user?.id, id, payload, subscriberId);
  };


  const renderCategoryName = (cat: any) => {
    if (editingId === cat?.id) {
      return (
        <>
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            size="sm"
            mr={2}
            width={"100%"}
            onClick={(e) => e.stopPropagation()}
          />
          {renderCheckIconUpdate(cat)}
        </>
      );
    } else {
      return (
        <>
          <Text
            fontWeight={cat?.expanded ? "bold" : 500}
            flex={1}
            color={cat?.expanded ? "#1819B4" : "#2F2F34"}
          >
            {cat?.title}
          </Text>
          {hoveredId === cat?.id && (
            <>
              <EditSvg
                color={"gray"}
                mr={2}
                title={t("categoryManagement.edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(cat?.id);
                  setEditingValue(cat?.title);
                }}
                style={{ cursor: "pointer" }}
              />
              {renderDeleteIcon(cat)}
            </>
          )}
        </>
      );
    }
  };

  const renderDeleteIcon = (cat: any) => {
    // if (deleteCategoryLoading) {
    //   return <Spinner />;
    // }
    return (
      <Image
        src={Delete}
        width={5}
        ml={3}
        color="red.400"
        onClick={(e) => {
          e.stopPropagation();
          setIsDeleteModal(true);
          setDeleteId(cat?.id);
        }}
        title={t("categoryManagement.delete")}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const renderCheckIconUpdate = (cat: any) => {
    if (updateCategoryLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon
        width={6}
        cursor={"pointer"}
        onClick={(e) => {
          e.stopPropagation();
          handleSaveEdit(cat);
        }}
      />
    );
  };

  const renderCheckIcon = () => {
    if (addCategoryLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon width={6} cursor={"pointer"} onClick={handleAddCategory} />
    );
  };

  const renderAddNewCategory = () => {
    if (addNewCategory)
      return (
        <Flex gap={2} align="center" mt={4}>
          <Input
            placeholder={t("deptManagement.enterCourseCategory")}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            width={"100%"}
            bg="white"
            borderRadius={"12px"}
            border={"1px solid #CDD6DC"}
          />
          {renderCheckIcon()}
          <Image
            src={Delete}
            width={5}
            ml={1}
            color="red.400"
            cursor={"pointer"}
            onClick={() => setAddNewCategory(false)}
          />
        </Flex>
      );
  };


  const renderCategoriesData = () => {
    if (getCategoriesLoading) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Spinner size={"lg"} />
        </Flex>
      );
    }
    if (!categoriesData?.rows?.length) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.500" textAlign="center" mt={4}>
            {t("categoryManagement.noCategoriesFound")}
          </Text>
        </Flex>
      );
    }

    return (
      <VStack
        align="stretch"
        spacing={1}
        mb={8}
        maxH={"600px"}
        overflowY={"auto"}
      >
        {categoriesData?.rows?.map((cat: any) => (
          <Box key={cat.id} bg={"transparent"} borderRadius="lg">
            <Flex
              align="center"
              px={2}
              py={2}
              _hover={{ bg: "#1819B414", borderRadius: "12px" }}
              cursor={"pointer"}
              onMouseEnter={() => setHoveredId(cat?.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {renderCategoryName(cat)}
            </Flex>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box bg="#FFFFFF" minH="100vh">
      <SearchInput
        data-tour-item="pledge-dept-mgmt-search"
        placeholder={t("categoryManagement.searchPlaceholder")}
        maxW="600px"
        value={search}
        onChange={handleSearch}
      />
      <Box mt={6} mb={4}>
        <Box data-tour-item="pledge-dept-mgmt-add-dept" mb={2}>
          <Button
            leftIcon={<AddIcon />}
            color="#1819B4"
            variant="link"
            fontStyle={"italic"}
            mb={2}
            onClick={() => setAddNewCategory(true)}
          >
            {t("deptManagement.addCategory")}
          </Button>
        </Box>
        {/* Render Input for add new category */}
        {renderAddNewCategory()}
        <Box data-tour-item="pledge-dept-mgmt-department-list">
          {renderCategoriesData()}
        </Box>
      </Box>
      <ModalDelete
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
        }}
        onConfirm={() => handleDeleteCategory()}
        title={t("categoryManagement.removeTitle")}
        description={t("categoryManagement.removeDescription")}
        chatCountDescription={t("categoryManagement.removeConfirm")}
        deleteButtonTitle={t("categoryManagement.Delete")}
        cancelButtonTitle={t("categoryManagement.cancel")}
      />
      <Paginate
        page={page}
        count={pageCount}
        pageSize={PAGINATION_DEFAULTS.PAGESIZE}
        onPageChange={handlePageChange}
        margin={1}
        variant="ghost"
      />
    </Box>
  );
};

export default DeptCategoryManagement;
